import React, { useEffect, useState, useRef } from "react";
import { Spin, Empty, Modal, Button } from "antd";
import axios from "axios";
import ProductListCard from "../Product/ProductListCard";
import ProductDetail from "../Product/ProductDetail";
import { useParams, useLocation } from "react-router-dom";
import "../Common/CollectionPage.css";

const NewArrival = ({ refreshCartCount }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showPrice, setShowPrice] = useState(!!localStorage.getItem("token"));
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const { categoryName } = useParams();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const mobileNumber = searchParams.get("mob");

  // Add a ref to track if mobile logging has been done
  const mobileLoggedRef = useRef(false);

  useEffect(() => {
    async function fetchNewArrivals() {
      setLoading(true);
      try {
        const tokenString = localStorage.getItem("token");
        const response = await axios.get(
          global.config.APIURL.BaseURL + "api/NewArrival/GetNewArrivals",
          {
            headers: {
              Authorization: "Bearer " + tokenString,
            },
          }
        );
        setData(response.data || []);
      } catch (error) {
        setData([]);
      } finally {
        setLoading(false);
      }
    }

    // Log mobile activity first if mobile number exists and hasn't been logged yet
    if (mobileNumber && !mobileLoggedRef.current) {
      logMobileActivity(mobileNumber);
      mobileLoggedRef.current = true;
    }

    fetchNewArrivals();
    logUserActivity();
  }, []); // Empty dependency array - runs only once on mount

  // Reset the ref when component unmounts
  useEffect(() => {
    return () => {
      mobileLoggedRef.current = false;
    };
  }, []);

  const logUserActivity = async () => {
    const tokenString = localStorage.getItem("token");

    try {
      const jsondata = JSON.stringify({
        ScreenID: "1004",
        DetailID: "0",
      });

      let config = {
        method: "post",
        url: global.config.APIURL.BaseURL + "api/UserActivity/InsertActivity",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
        data: jsondata,
      };

      axios
        .request(config)
        .then((response) => {})
        .catch((error) => {});
    } catch (err) {}
  };

  // Add this helper function to clean mobile number
  const cleanMobileNumber = (mobileNo) => {
    if (!mobileNo) return null;

    // Remove all non-digit characters
    let cleaned = mobileNo.replace(/\D/g, "");

    // Remove leading +91 or 91
    if (cleaned.startsWith("91") && cleaned.length > 10) {
      cleaned = cleaned.substring(2);
    }

    // Check if it's exactly 10 digits
    if (cleaned.length === 10) {
      return cleaned;
    }

    return null; // Invalid mobile number
  };

  // Update the logMobileActivity function
  const logMobileActivity = async (mobileNo) => {
    try {
      const cleanedMobile = cleanMobileNumber(mobileNo);

      if (!cleanedMobile) {
        console.error("Invalid mobile number format:", mobileNo);
        return;
      }
      console.log(cleanedMobile);
      const config = {
        method: "post",
        url: `${global.config.APIURL.BaseURL}api/PublicData/LogURL?MobileNo=${cleanedMobile}&ScreenID=1004`,
        headers: {
          "Content-Type": "application/json",
        },
      };

      await axios.request(config);
    } catch (error) {
      console.error("Error logging mobile activity:", error);
    }
  };

  // All category names for filter chips
  const [allCategoryNames, setAllCategoryNames] = useState([]);

  // Set selected category from URL param after data is loaded
  useEffect(() => {
    if (data.length > 0) {
      const allCategoryNames = data.map((item) => item.Category);
      setAllCategoryNames(allCategoryNames);

      // Handle category from URL parameter
      if (categoryName) {
        // Decode URL-encoded category name and clean it
        let decodedCategoryName = decodeURIComponent(categoryName);

        // Handle malformed URL where & is used instead of ?
        if (decodedCategoryName.includes("&mob=")) {
          decodedCategoryName = decodedCategoryName.split("&mob=")[0];
        }

        console.log("Decoded category name:", decodedCategoryName);

        // Check if the decoded category exists in the data (case-insensitive)
        const categoryExists = allCategoryNames.find(
          (cat) => cat.toLowerCase() === decodedCategoryName.toLowerCase()
        );

        if (categoryExists) {
          console.log("Category found, selecting:", categoryExists);
          setSelectedCategories([categoryExists]);
        } else {
          console.log("Category not found, selecting all categories");
          console.log("Available categories:", allCategoryNames);
          setSelectedCategories(allCategoryNames);
        }
      } else {
        // If no category in URL, select all categories
        setSelectedCategories(allCategoryNames);
      }
    }
  }, [data, categoryName]);

  // Update the mobile number extraction to handle both URL formats
  const extractMobileFromUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    let mobileFromQuery = searchParams.get("mob");

    // If not found in query params, check if it's in the path (malformed URL)
    if (!mobileFromQuery && categoryName && categoryName.includes("&mob=")) {
      const parts = categoryName.split("&mob=");
      mobileFromQuery = parts[1];
    }

    return mobileFromQuery;
  };

  // Update the mobile logging logic
  useEffect(() => {
    const mobileNumber = extractMobileFromUrl();

    if (mobileNumber && !mobileLoggedRef.current) {
      logMobileActivity(mobileNumber);
      mobileLoggedRef.current = true;
    }
  }, [categoryName, location.search]);

  // Handle "All" chip state
  const isAllChecked =
    selectedCategories.length === allCategoryNames.length &&
    allCategoryNames.length > 0;

  // Filtered categories to display:
  const filteredData =
    selectedCategories.length === allCategoryNames.length
      ? allCategoryNames
          .map((catName) => data.find((cat) => cat.Category === catName))
          .filter(Boolean)
      : [...selectedCategories]
          .reverse()
          .map((catName) => data.find((cat) => cat.Category === catName))
          .filter(Boolean);

  useEffect(() => {
    const updateShowPrice = () => setShowPrice(!!localStorage.getItem("token"));
    updateShowPrice();
    window.addEventListener("storage", updateShowPrice);
    return () => window.removeEventListener("storage", updateShowPrice);
  }, []);

  // Handler for product card click
  const handleProductCardClick = (product) => {
    setSelectedProductId(product.ProductID);
    setShowProductModal(true);
  };

  const toggleCategory = (cat) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  return (
    <div className="collection-page">
      <div className="collection-header">
        <h1>New Arrivals</h1>
        <p>Discover our latest designs, just in</p>
      </div>

      {!loading && allCategoryNames.length > 0 && (
        <div className="collection-chips">
          <button
            type="button"
            className={`collection-chip ${
              isAllChecked ? "collection-chip--active" : ""
            }`}
            onClick={() =>
              setSelectedCategories(isAllChecked ? [] : allCategoryNames)
            }
          >
            All
          </button>
          {allCategoryNames.map((cat) => (
            <button
              key={cat}
              type="button"
              className={`collection-chip ${
                selectedCategories.includes(cat) ? "collection-chip--active" : ""
              }`}
              onClick={() => toggleCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 72 }}>
          <Spin size="large" />
        </div>
      ) : data.length === 0 ? (
        <Empty
          description="No new arrivals found"
          style={{ padding: "64px 0" }}
        />
      ) : filteredData.length === 0 ? (
        <Empty
          description="No categories selected"
          style={{ padding: "64px 0" }}
        />
      ) : (
        filteredData.map((category) => (
          <section className="collection-section" key={category.Category}>
            <div className="collection-section__head">
              <h2 className="collection-section__title">{category.Category}</h2>
              <span className="collection-section__rule" />
              <span className="collection-section__count">
                {category.Products.length} items
              </span>
            </div>
            <div className="collection-grid">
              {category.Products.map((product) => (
                <ProductListCard
                  key={product.ProductID}
                  product={product}
                  showProdStatus
                  showPrice={showPrice}
                  onClick={handleProductCardClick}
                />
              ))}
            </div>
          </section>
        ))
      )}

      {/* Product Detail Modal */}
      <Modal
        open={showProductModal}
        onCancel={() => {
          setShowProductModal(false);
          setSelectedProductId(null);
        }}
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "16px 0 0 0",
            }}
          >
            <Button
              type="primary"
              style={{
                minWidth: 120,
                borderRadius: 6,
                background: "#ff4d4f",
                borderColor: "#ff4d4f",
              }}
              onClick={() => {
                setShowProductModal(false);
                setSelectedProductId(null);
              }}
            >
              Close
            </Button>
          </div>
        }
        width="90%"
        style={{ maxWidth: 900 }}
        destroyOnClose
        styles={{ padding: 0 }}
      >
        {showProductModal && selectedProductId && (
          <ProductDetail
            refreshCartCount={refreshCartCount}
            productIdQuery={selectedProductId}
            isModal
            setDocumentTitle={() => {}}
          />
        )}
      </Modal>
    </div>
  );
};

export default NewArrival;
